/**
 * What a set actually moved.
 *
 * `weight_kg` is what you put on the bar, the belt or the stack — and on two
 * families of exercise that is not the load. Where the person is part of the
 * lift the app has to say so explicitly, because every comparison it makes is a
 * `>` and both of these once pointed the wrong way:
 *
 *   * an assist machine SUBTRACTS, so the personal record went to whoever took
 *     the most help, and moving the pin down read as getting weaker;
 *   * a pull up's weight box means ADDED load and is nearly always empty, so a
 *     set of eight was arithmetically nothing — no estimated 1RM, no volume,
 *     and a "heaviest set" record reading 0 lb.
 *
 * One formula covers both, and mirrors the server's `SetLog.recompute_derived`
 * exactly:
 *
 *     load = body weight x factor  ±  weight_kg
 *
 * Pure on purpose: `db/index.ts` re-exports these, but this module imports no
 * Dexie, so it can be unit-tested under node:test.
 */

import type { Exercise, SetRow } from '../db/index.ts'

/**
 * How much of the lifter this exercise moves, 0..1.
 *
 * Zero for everything on a bar or a stack, and for a row cached by a client too
 * old to have been sent the field — which is the safe direction, since it is
 * exactly today's behaviour. One for a pull up, a chin up, a dip and the assist
 * machines. Nothing in between: see BODYWEIGHT_FACTOR in
 * app/data/exercises.py for why a push up is deliberately left at zero.
 */
export function bodyweightFactor(exercise: Exercise | null | undefined): number {
  return exercise?.bodyweight_factor ?? 0
}

/**
 * The load in kg, or null when it cannot be known.
 *
 * Prefers the server's `effective_weight_kg`, which snapshots the body weight of
 * the day the set happened. `bodyweightKg` covers the set logged thirty seconds
 * ago that has not synced yet; with neither, the set contributes nothing rather
 * than contributing an empty weight box as if it were the load.
 */
export function movedWeightKg(
  set: Pick<SetRow, 'weight_kg' | 'effective_weight_kg'>,
  exercise: Exercise | null | undefined,
  bodyweightKg?: number | null,
): number | null {
  const factor = bodyweightFactor(exercise)
  if (factor <= 0) return set.weight_kg ?? null
  if (set.effective_weight_kg != null) return set.effective_weight_kg
  if (bodyweightKg == null) return null
  const base = bodyweightKg * factor
  const own = set.weight_kg ?? 0
  return exercise?.is_assisted ? Math.max(base - own, 0) : base + own
}
